
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { CoursePerformance } from "@/services/salesService";
import { cn } from "@/lib/utils";
import { ErrorDisplay } from "@/components/public-course/ErrorDisplay";
import { formatPrice, formatNumber } from "@/utils/formatters";

interface CoursePerformanceChartProps {
  data: CoursePerformance[];
  isLoading: boolean;
  filter: string;
  onFilterChange: (value: string) => void;
}

export function CoursePerformanceChart({
  data,
  isLoading,
  filter,
  onFilterChange
}: CoursePerformanceChartProps) {
  const hasData = data && data.length > 0;

  // Sort data based on the selected filter
  const sortedData = [...(data || [])].sort((a, b) => {
    if (filter === 'revenue') return b.revenue - a.revenue;
    if (filter === 'sales') return b.sales - a.sales;
    if (filter === 'change') return b.change - a.change;
    return 0;
  });

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h3 className="text-lg font-semibold">Course Performance</h3>
          <Select value={filter} onValueChange={onFilterChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="revenue">Highest Revenue</SelectItem>
                <SelectItem value="sales">Most Sales</SelectItem>
                <SelectItem value="change">Highest Growth</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="hidden md:block h-[300px] mb-6">
          {isLoading ? (
            <div className="h-full w-full flex items-center justify-center">
              <p className="text-muted-foreground">Loading data...</p>
            </div>
          ) : !hasData ? (
            <div className="h-full w-full flex items-center justify-center flex-col gap-2">
              <p className="text-muted-foreground">No course data available for the selected period</p>
              <p className="text-sm text-muted-foreground">Add courses and generate sales to see performance metrics</p>
            </div>
          ) : (
            <ChartContainer 
              className="h-full"
              config={{
                revenue: { color: "#8884d8" },
                sales: { color: "#82ca9d" }
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={sortedData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="left" 
                    orientation="left" 
                    stroke="#8884d8" 
                    label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => `$${formatPrice(value, 0)}`}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    stroke="#82ca9d" 
                    label={{ value: 'Sales (units)', angle: -90, position: 'insideRight' }}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend wrapperStyle={{ paddingTop: 20 }} />
                  <Bar yAxisId="left" dataKey="revenue" name="Revenue ($)" fill="#8884d8" />
                  <Bar yAxisId="right" dataKey="sales" name="Sales (units)" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}
        </div>
        
        <div className="md:hidden space-y-4">
          {isLoading ? (
            <p className="text-center text-muted-foreground py-4">Loading data...</p>
          ) : !hasData ? (
            <div className="text-center py-4">
              <p className="text-muted-foreground">No course data available</p>
              <p className="text-sm text-muted-foreground mt-1">Add courses and generate sales to see performance metrics</p>
            </div>
          ) : (
            sortedData.map((course, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{course.name}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatNumber(course.sales)} sales
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${formatPrice(course.revenue)}</p>
                      <p className={cn(
                        "text-xs mt-1",
                        course.change >= 0 ? "text-green-600" : "text-red-600"
                      )}>
                        {course.change >= 0 ? "+" : ""}{course.change}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
